import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';

/**
 * One editable field, with an explicit "change this" toggle.
 *
 * The toggle is not decoration. Without it there is no way to tell "leave this
 * field alone" from "set this field to empty", and a bulk edit that silently
 * blanks the fields you didn't fill in is the classic way this feature
 * destroys data.
 */
export interface BulkFieldSpec {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'date';
  options?: Array<{ value: string; label: string }>;
  hint?: string;
  /** Coerce the raw input string into what the API expects. Defaults by type. */
  parse?: (raw: string) => unknown;
}

/** The response shape both bulk endpoints return. */
export interface BulkResultRow {
  id: string;
  status: 'updated' | 'failed';
  reason?: string;
  [key: string]: unknown;
}

export interface BulkResult {
  requested: number;
  updated: number;
  failed: number;
  fields: string[];
  results: BulkResultRow[];
}

interface BulkFieldEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ids: string[];
  fields: BulkFieldSpec[];
  /** Singular noun for the records being edited, e.g. "item", "asset". */
  noun: string;
  submit: (ids: string[], changes: Record<string, unknown>) => Promise<BulkResult>;
  /** How a failed row is named in the results list. */
  describeRow: (row: BulkResultRow) => { title: string; subtitle?: string };
  /** Called after a run that changed at least one record. */
  onApplied: (result: BulkResult) => void;
}

/**
 * Edit one set of fields across many records, and report what happened to each.
 *
 * Shared by the inventory and equipment lists. Both bulk endpoints apply changes
 * per record rather than in one transaction and return the same result shape, so
 * the partial-success case — which is the normal one — is presented identically
 * in both places.
 */
export function BulkFieldEditDialog({
  open,
  onOpenChange,
  ids,
  fields,
  noun,
  submit,
  describeRow,
  onApplied,
}: BulkFieldEditDialogProps) {
  const [enabled, setEnabled] = useState<Set<string>>(new Set());
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset on open so a previous run's selections and results never leak into
  // the next one.
  useEffect(() => {
    if (open) {
      setEnabled(new Set());
      setValues({});
      setResult(null);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const toggleField = (key: string) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const changes: Record<string, unknown> = {};
  for (const field of fields) {
    if (!enabled.has(field.key)) continue;
    const raw = values[field.key];
    if (raw === undefined || raw === '') continue;
    if (field.parse) changes[field.key] = field.parse(raw);
    else if (field.type === 'number') changes[field.key] = Number(raw);
    else if (field.type === 'date') changes[field.key] = new Date(raw).toISOString();
    else changes[field.key] = raw;
  }
  const changeCount = Object.keys(changes).length;
  const plural = ids.length > 1 ? 's' : '';

  const handleApply = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await submit(ids, changes);
      setResult(res);
      if (res.updated > 0) onApplied(res);
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message ??
        'The update could not be applied';
      setError(Array.isArray(message) ? message.join('. ') : message);
    } finally {
      setSubmitting(false);
    }
  };

  const failures = result?.results.filter((r) => r.status === 'failed') ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {result.failed === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-status-success-foreground" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-status-warning-foreground" />
                )}
                {result.updated} of {result.requested} updated
              </DialogTitle>
              <DialogDescription>
                {result.failed === 0
                  ? `Changed ${result.fields.join(', ')} on every selected ${noun}.`
                  : `${result.failed} ${noun}${result.failed > 1 ? 's were' : ' was'} left unchanged. Each reason is below.`}
              </DialogDescription>
            </DialogHeader>

            {failures.length > 0 && (
              <ScrollArea className="max-h-64 rounded-md border">
                <div className="divide-y">
                  {failures.map((row) => {
                    const described = describeRow(row);
                    return (
                      <div key={row.id} className="px-3 py-2">
                        <p className="text-sm font-medium">{described.title}</p>
                        {described.subtitle && (
                          <p className="text-xs text-muted-foreground">{described.subtitle}</p>
                        )}
                        <p className="mt-1 text-xs text-status-destructive-foreground">
                          {row.reason}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                Edit {ids.length} {noun}
                {plural}
              </DialogTitle>
              <DialogDescription>
                Tick a field to change it. Anything left unticked keeps its current value on every
                selected {noun}.
              </DialogDescription>
            </DialogHeader>

            <ScrollArea className="max-h-[22rem] pr-3">
              <div className="space-y-3">
                {fields.map((field) => {
                  const on = enabled.has(field.key);
                  return (
                    <div key={field.key} className="flex items-start gap-3">
                      <Checkbox
                        id={`bulk-${field.key}`}
                        checked={on}
                        onCheckedChange={() => toggleField(field.key)}
                        className="mt-2.5"
                      />
                      <div className="min-w-0 flex-1">
                        <Label
                          htmlFor={`bulk-${field.key}`}
                          className={on ? '' : 'text-muted-foreground'}
                        >
                          {field.label}
                        </Label>
                        <div className="mt-1">
                          {field.type === 'select' ? (
                            <Select
                              disabled={!on}
                              value={values[field.key] ?? ''}
                              onValueChange={(v) =>
                                setValues((prev) => ({ ...prev, [field.key]: v }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select…" />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options?.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              type={
                                field.type === 'number'
                                  ? 'number'
                                  : field.type === 'date'
                                    ? 'date'
                                    : 'text'
                              }
                              min={field.type === 'number' ? 0 : undefined}
                              disabled={!on}
                              value={values[field.key] ?? ''}
                              onChange={(e) =>
                                setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                              }
                              placeholder={on ? 'New value' : 'Unchanged'}
                            />
                          )}
                        </div>
                        {field.hint && on && (
                          <p className="mt-1 text-xs text-muted-foreground">{field.hint}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {error && (
              <p className="text-sm text-status-destructive-foreground" role="alert">
                {error}
              </p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleApply} disabled={changeCount === 0 || submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {changeCount === 0 ? 'Choose a field' : `Apply to ${ids.length} ${noun}${plural}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
